import axios from 'axios';
import { reportLabels } from '../constants/reportLabels';
import type { ReportProblem } from '../types';

const REPORT_ERROR = 'com.mylaborelec.api.reports.';
const BUSY_TYPE = `${REPORT_ERROR}busy`;
// Request validation errors of the reports routes: the detail lists fields and rules, never the values sent.
const INVALID_REQUEST_TYPE = 'com.mylaborelec.api.invalid-request';

const MESSAGES_BY_TYPE: Record<string, string> = {
  [`${REPORT_ERROR}no-up-to-date-results`]: reportLabels.error_no_up_to_date_results,
  [`${REPORT_ERROR}archive-failed`]: reportLabels.error_archive_failed,
  // A 503 without Retry-After: unlike `busy`, retrying at once would most likely time out again.
  [`${REPORT_ERROR}render-timeout`]: reportLabels.error_render_timeout,
  [`${REPORT_ERROR}rendering-failed`]: reportLabels.error_rendering_failed,
  [`${REPORT_ERROR}not-found`]: reportLabels.error_not_found,
  [`${REPORT_ERROR}download-failed`]: reportLabels.error_download_failed,
  [INVALID_REQUEST_TYPE]: reportLabels.error_invalid_request,
};

const MESSAGES_BY_STATUS: Record<number, string> = {
  404: reportLabels.error_not_found,
  409: reportLabels.error_no_up_to_date_results,
  413: reportLabels.error_too_large,
  422: reportLabels.error_invalid_request,
  500: reportLabels.error_rendering_failed,
  503: reportLabels.error_busy,
};

/** `Retry-After` is either a number of seconds or an HTTP date. */
const parseRetryAfter = (value: unknown): number | null => {
  if (typeof value !== 'string' || !value.trim()) return null;
  const seconds = Number(value);
  if (Number.isFinite(seconds)) return Math.max(0, Math.ceil(seconds));
  const date = Date.parse(value);
  return Number.isNaN(date) ? null : Math.max(0, Math.ceil((date - Date.now()) / 1000));
};

/** Blob responses (file downloads) carry their JSON error body as a Blob too. */
const readBody = async (data: unknown): Promise<Record<string, unknown> | null> => {
  if (data instanceof Blob) {
    try {
      return JSON.parse(await data.text());
    } catch {
      return null;
    }
  }
  return data && typeof data === 'object' ? (data as Record<string, unknown>) : null;
};

export const readReportProblem = async (error: unknown): Promise<ReportProblem> => {
  if (!axios.isAxiosError(error) || !error.response) return { status: null, type: null, retryAfterSeconds: null };
  const body = await readBody(error.response.data);
  const type = typeof body?.type === 'string' && body.type !== 'about:blank' ? body.type : null;
  return { status: error.response.status, type, retryAfterSeconds: parseRetryAfter(error.response.headers['retry-after']) };
};

/** Readable message chosen from the error `type` first, then the HTTP status; server text is never shown. */
export const reportErrorMessage = (problem: ReportProblem, fallback: string = reportLabels.error_unknown): string => {
  if (problem.type === BUSY_TYPE) {
    return problem.retryAfterSeconds ? reportLabels.error_busy_retry_after(problem.retryAfterSeconds) : reportLabels.error_busy;
  }
  const byType = problem.type ? MESSAGES_BY_TYPE[problem.type] : undefined;
  return byType ?? (problem.status ? MESSAGES_BY_STATUS[problem.status] : undefined) ?? fallback;
};
